import { useCallback, useState } from "react";
import { BarChart3, CalendarCheck, DollarSign, Flag, LogOut, Megaphone, Trophy, Users } from "lucide-react";
import { KindredMark } from "./KindredMark";
import { useAdminAuth } from "../hooks/useAdminAuth";
import { AdminLoginForm } from "./admin/AdminLoginForm";
import { OverviewTab } from "./admin/OverviewTab";
import { MembersTab } from "./admin/MembersTab";
import { ModerationTab } from "./admin/ModerationTab";
import { AnalyticsTab } from "./admin/AnalyticsTab";
import { EventsTab } from "./admin/EventsTab";
import { NotifyTab } from "./admin/NotifyTab";
import { RevenueTab } from "./admin/RevenueTab";

const TABS = [
  { id: "overview", label: "Overview", icon: Trophy, Component: OverviewTab },
  { id: "members", label: "Members", icon: Users, Component: MembersTab },
  { id: "moderation", label: "Moderation", icon: Flag, Component: ModerationTab },
  { id: "analytics", label: "Analytics", icon: BarChart3, Component: AnalyticsTab },
  { id: "events", label: "Events", icon: CalendarCheck, Component: EventsTab },
  { id: "notify", label: "Notify", icon: Megaphone, Component: NotifyTab },
  { id: "revenue", label: "Revenue", icon: DollarSign, Component: RevenueTab },
];

const TabBar = ({ tab, setTab }) => (
  <div className="mb-10 flex flex-wrap gap-2" data-testid="admin-tabs">
    {TABS.map((t) => (
      <button
        key={t.id}
        onClick={() => setTab(t.id)}
        data-testid={`admin-tab-${t.id}`}
        className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-300 active:scale-95 ${
          tab === t.id ? "border-gold bg-gold text-ink" : "border-cream/20 text-cream/60 hover:border-gold hover:text-gold"
        }`}
      >
        <t.icon className="h-3.5 w-3.5" />
        {t.label}
      </button>
    ))}
  </div>
);

const DashboardHeader = ({ onLogout }) => (
  <header className="border-b border-cream/10">
    <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
      <div className="flex items-center gap-2">
        <KindredMark className="h-7 w-7" />
        <span className="font-serif text-xl font-semibold tracking-tight">Waitlist Dashboard</span>
      </div>
      <div className="flex items-center gap-4">
        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-cream/40" data-testid="admin-live-indicator">
          <span className="h-2 w-2 animate-pulse rounded-full bg-gold" />
          Live
        </span>
        <button onClick={onLogout} data-testid="admin-logout-button" className="flex items-center gap-2 rounded-full border border-cream/20 px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] text-cream/70 transition-colors duration-300 hover:border-gold hover:text-gold">
          <LogOut className="h-3.5 w-3.5" />
          Sign out
        </button>
      </div>
    </div>
  </header>
);

export const AdminPage = () => {
  const { admin, login, logout, expire } = useAdminAuth();
  const [tab, setTab] = useState("overview");
  const [fetchError, setFetchError] = useState("");
  const onError = useCallback((msg) => setFetchError(msg), []);

  if (admin === null) {
    return <div className="flex min-h-screen items-center justify-center bg-obsidian text-sm text-cream/50" data-testid="admin-checking">Checking session…</div>;
  }
  if (!admin) return <AdminLoginForm onLogin={login} />;

  const Active = TABS.find((t) => t.id === tab).Component;

  return (
    <div className="min-h-screen bg-obsidian text-cream" data-testid="admin-dashboard">
      <DashboardHeader onLogout={logout} />
      <main className="mx-auto max-w-6xl px-6 py-12">
        {fetchError && <p className="mb-6 text-sm font-medium text-red-400" data-testid="admin-fetch-error">{fetchError}</p>}
        <TabBar tab={tab} setTab={setTab} />
        <Active onExpire={expire} onError={onError} />
      </main>
    </div>
  );
};
